import { useCallback, useEffect, useMemo, useState } from "react";
import { Eye, Loader2, MessageCircle, Phone, RefreshCw, Search, Trash2, X } from "lucide-react";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Converted", "Not Interested", "Spam"];

function fmtDate(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return value;
  }
}

function phoneDigits(value) {
  return String(value || "").replace(/\D+/g, "");
}

function statusTone(status) {
  if (status === "Converted") return "bg-green-50 text-green-700 border-green-200";
  if (status === "Contacted") return "bg-blue-50 text-blue-700 border-blue-200";
  if (status === "Not Interested") return "bg-amber-50 text-amber-700 border-amber-200";
  if (status === "Spam") return "bg-red-50 text-red-700 border-red-200";
  return "bg-slate-950 text-white border-slate-950";
}

export default function AdminWebsiteEnquiries() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [sources, setSources] = useState([]);
  const [filters, setFilters] = useState({ search: "", status: "", source: "", start: "", end: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [busyId, setBusyId] = useState("");

  const params = useMemo(() => {
    const clean = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value) clean[key] = value;
    });
    return clean;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/website-enquiries", { params });
      setRows(data.items || []);
      setCounts(data.counts || {});
      setSources(data.sources || []);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail || err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    const timer = window.setTimeout(load, 250);
    return () => window.clearTimeout(timer);
  }, [load]);

  const view = async (row) => {
    setBusyId(row.id);
    try {
      const { data } = await api.get(`/admin/website-enquiries/${row.id}`);
      setSelected(data);
      setRows((current) => current.map((item) => item.id === row.id ? { ...item, isRead: true } : item));
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail || err));
    } finally {
      setBusyId("");
    }
  };

  const updateStatus = async (row, status) => {
    setBusyId(row.id);
    try {
      const { data } = await api.patch(`/admin/website-enquiries/${row.id}/status`, { status });
      setRows((current) => current.map((item) => item.id === row.id ? data : item));
      setSelected((current) => current?.id === row.id ? data : current);
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail || err));
    } finally {
      setBusyId("");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete enquiry from ${row.name || row.mobile}?`)) return;
    setBusyId(row.id);
    try {
      await api.delete(`/admin/website-enquiries/${row.id}`);
      setRows((current) => current.filter((item) => item.id !== row.id));
      setSelected((current) => current?.id === row.id ? null : current);
      toast.success("Enquiry deleted");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail || err));
    } finally {
      setBusyId("");
    }
  };

  return (
    <div>
      <PageHeader overline="Admin / Website" title="Website enquiries." subtitle="Contact form submissions from RP Signage website, latest first." />

      <div className="mb-5 grid gap-3 rounded-sm border border-[#E5E7EB] bg-white p-4 md:grid-cols-[1.5fr_repeat(4,1fr)_auto]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]" />
          <Input className="pl-9" placeholder="Search name, mobile, email, message" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
        </div>
        <select className="h-10 rounded-md border border-[#E5E7EB] px-3 text-sm" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All Status</option>
          {STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
        </select>
        <select className="h-10 rounded-md border border-[#E5E7EB] px-3 text-sm" value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })}>
          <option value="">All Source</option>
          {sources.map((source) => <option key={source} value={source}>{source}</option>)}
        </select>
        <Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} />
        <Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} />
        <Button variant="outline" className="rounded-sm" onClick={load} disabled={loading}><RefreshCw className="mr-2 h-4 w-4" />Refresh</Button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2 text-sm">
        <Badge label="Filtered" value={counts.filtered ?? rows.length} />
        <Badge label="New" value={counts.new ?? 0} />
        <Badge label="Spam" value={counts.spam ?? 0} />
        <button type="button" onClick={() => setFilters({ ...filters, status: "Spam" })} className="rounded-full border border-red-200 bg-red-50 px-3 py-1 font-black text-red-700">Spam Filter</button>
      </div>

      {error && <div className="mb-4 rounded-sm border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">{error}</div>}
      {loading ? (
        <div className="grid place-items-center rounded-sm border border-[#E5E7EB] bg-white p-12 text-[#6B7280]"><Loader2 className="mb-2 h-5 w-5 animate-spin" />Loading enquiries...</div>
      ) : (
        <>
          <div className="hidden overflow-x-auto rounded-sm border border-[#E5E7EB] bg-white lg:block">
            <table className="min-w-[1280px] w-full text-left text-sm">
              <thead className="bg-[#F9FAFB] text-[11px] uppercase tracking-[0.18em] text-[#6B7280]">
                <tr>
                  {["Date & Time", "Name", "Mobile", "Email", "Service/Product Interest", "Message", "Page URL", "Source", "Device", "Status", "Actions"].map((head) => <th key={head} className="px-3 py-3">{head}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => <EnquiryRow key={row.id} row={row} busy={busyId === row.id} onView={view} onStatus={updateStatus} onDelete={remove} />)}
                {!rows.length && <tr><td colSpan={11} className="px-3 py-10 text-center text-[#6B7280]">No enquiries found.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="grid gap-3 lg:hidden">
            {rows.map((row) => <EnquiryCard key={row.id} row={row} busy={busyId === row.id} onView={view} onStatus={updateStatus} onDelete={remove} />)}
            {!rows.length && <div className="rounded-sm border border-[#E5E7EB] bg-white p-8 text-center text-sm text-[#6B7280]">No enquiries found.</div>}
          </div>
        </>
      )}

      {selected && <EnquiryDrawer row={selected} busy={busyId === selected.id} onClose={() => setSelected(null)} onStatus={updateStatus} onDelete={remove} />}
    </div>
  );
}

function Badge({ label, value }) {
  return <span className="rounded-full border border-[#E5E7EB] bg-white px-3 py-1 font-black text-[#111827]">{label}: {value}</span>;
}

function StatusSelect({ row, disabled, onStatus }) {
  return (
    <select className="h-9 rounded-sm border border-[#E5E7EB] px-2 text-xs font-semibold" value={row.status || "New"} disabled={disabled} onChange={(e) => onStatus(row, e.target.value)}>
      {STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
    </select>
  );
}

function ActionButtons({ row, busy, onView, onStatus, onDelete }) {
  const digits = phoneDigits(row.mobile);
  return (
    <div className="flex flex-wrap gap-1">
      <button className="h-9 w-9 rounded-sm border border-[#E5E7EB]" title="View full enquiry" onClick={() => onView(row)} disabled={busy}>{busy ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : <Eye className="mx-auto h-4 w-4" />}</button>
      <a className="grid h-9 w-9 place-items-center rounded-sm border border-[#E5E7EB] text-blue-700" title="Call" href={digits ? `tel:${digits}` : "#"}><Phone className="h-4 w-4" /></a>
      <a className="grid h-9 w-9 place-items-center rounded-sm border border-green-200 text-green-700" title="WhatsApp" href={digits ? `https://wa.me/${digits}` : "#"} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4" /></a>
      <button className="h-9 rounded-sm border border-red-200 px-2 text-xs font-black text-red-600" onClick={() => onStatus(row, "Spam")} disabled={busy}>Spam</button>
      <button className="h-9 w-9 rounded-sm border border-red-200 text-red-600" title="Delete" onClick={() => onDelete(row)} disabled={busy}><Trash2 className="mx-auto h-4 w-4" /></button>
    </div>
  );
}

function StatusPill({ status }) {
  return <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-black ${statusTone(status || "New")}`}>{status || "New"}</span>;
}

function EnquiryRow({ row, busy, onView, onStatus, onDelete }) {
  return (
    <tr className={`border-t border-[#F3F4F6] align-top ${row.isRead === false ? "bg-blue-50/40" : ""}`}>
      <td className="px-3 py-3">{fmtDate(row.createdAt)}</td>
      <td className="px-3 py-3 font-black">{row.name || "-"}</td>
      <td className="px-3 py-3">{row.mobile || "-"}</td>
      <td className="px-3 py-3">{row.email || "-"}</td>
      <td className="px-3 py-3">{row.service || row.organization || "-"}</td>
      <td className="max-w-[260px] px-3 py-3"><div className="line-clamp-3">{row.message || "-"}</div></td>
      <td className="max-w-[220px] break-words px-3 py-3 text-xs">{row.pageUrl || "-"}</td>
      <td className="px-3 py-3">{row.source || "-"}</td>
      <td className="px-3 py-3">{row.deviceType || "-"}</td>
      <td className="px-3 py-3"><div className="grid gap-2"><StatusPill status={row.status} /><StatusSelect row={row} disabled={busy} onStatus={onStatus} /></div></td>
      <td className="px-3 py-3"><ActionButtons row={row} busy={busy} onView={onView} onStatus={onStatus} onDelete={onDelete} /></td>
    </tr>
  );
}

function EnquiryCard({ row, busy, onView, onStatus, onDelete }) {
  return (
    <div className={`rounded-sm border border-[#E5E7EB] bg-white p-4 text-sm ${row.isRead === false ? "ring-2 ring-blue-100" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-black">{row.name || "-"}</div>
          <div className="text-[#6B7280]">{fmtDate(row.createdAt)}</div>
        </div>
        <StatusPill status={row.status} />
      </div>
      <div className="mt-3 grid gap-1">
        <div><b>Mobile:</b> {row.mobile || "-"}</div>
        <div><b>Email:</b> {row.email || "-"}</div>
        <div><b>Interest:</b> {row.service || row.organization || "-"}</div>
        <div className="line-clamp-3"><b>Message:</b> {row.message || "-"}</div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <StatusSelect row={row} disabled={busy} onStatus={onStatus} />
        <ActionButtons row={row} busy={busy} onView={onView} onStatus={onStatus} onDelete={onDelete} />
      </div>
    </div>
  );
}

function EnquiryDrawer({ row, busy, onClose, onStatus, onDelete }) {
  return (
    <div className="fixed inset-0 z-[80] bg-black/30" onClick={onClose}>
      <aside className="ml-auto h-full w-full max-w-xl overflow-y-auto bg-white p-5 shadow-2xl" onClick={(event) => event.stopPropagation()}>
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.18em] text-[#6B7280]">Full enquiry</div>
            <h2 className="mt-1 text-2xl font-black">{row.name || "Website enquiry"}</h2>
            <p className="text-sm text-[#6B7280]">{fmtDate(row.createdAt)}</p>
          </div>
          <button className="h-10 w-10 rounded-sm border border-[#E5E7EB]" onClick={onClose}><X className="mx-auto h-5 w-5" /></button>
        </div>
        <div className="grid gap-3">
          <Detail label="Mobile" value={row.mobile} />
          <Detail label="Email" value={row.email} />
          <Detail label="Business / Organisation" value={row.organization} />
          <Detail label="Service / Product Interest" value={row.service} />
          <Detail label="Message" value={row.message} large />
          <Detail label="Page URL" value={row.pageUrl} />
          <Detail label="Source" value={row.source} />
          <Detail label="Device" value={row.deviceType} />
          <Detail label="Referrer" value={row.referrer} />
          <Detail label="IP Address" value={row.ipAddress} />
          <Detail label="User Agent" value={row.userAgent} large />
        </div>
        <div className="mt-5 flex flex-wrap gap-2 border-t border-[#E5E7EB] pt-4">
          <StatusSelect row={row} disabled={busy} onStatus={onStatus} />
          <ActionButtons row={row} busy={busy} onView={() => {}} onStatus={onStatus} onDelete={onDelete} />
        </div>
      </aside>
    </div>
  );
}

function Detail({ label, value, large = false }) {
  return (
    <div className="rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] p-3">
      <div className="text-[11px] font-black uppercase tracking-[0.16em] text-[#6B7280]">{label}</div>
      <div className={`mt-1 break-words font-semibold text-[#111827] ${large ? "whitespace-pre-wrap leading-7" : ""}`}>{value || "-"}</div>
    </div>
  );
}
